import { IUser } from 'core-components/sitebuilder/models/IUser'
import { IExceptionStatus } from 'core-components/sitebuilder/models/IUserRuleExceptions'
import { parseData } from 'src/core/classes/helpers'
import { getAppId } from 'src/core/classes/helpers/getAppId'
import { guid } from 'src/core/classes/helpers/GuidHelper'
import { arrayUnique } from 'src/core/classes/helpers/Helper'
import Logger from 'src/core/utils/Logger'

export interface IUserAvatar {
  Thumbnail: string
  Original: string
  Normal: string
  PublicId: string
}

// TODO: Move to lut
export interface IUserView {
  Id: string
  FirstName: string
  LastName: string
  Country: string
  Language: string
  Locale: string
  HpOrg: string
  GeoMarket: string
}

const nullableString = null as string | null

const initialData: IUser = {
  id: '',
  UID: guid(),
  canSimulateUsers: false,
  email: '',
  firstName: '',
  lastName: '',
  country: '',
  locale: '',
  hpOrg: '',
  geoMarket: '',
  language: '',
  countries: [] as string[],
  locales: [] as string[],
  languages: [] as string[],
  hpOrgs: [] as string[],
  geoMarkets: [] as string[],
  roleIds: [] as string[],
  ssoData: [] as any[],
  password: '',
  canBeSimulated: '',
  lastLogonTime: '',
  avatar: null as IUserAvatar | null,
  exceptionStatus: null as IExceptionStatus[] | null,
  AD_Manager: nullableString,
  AD_ManagerName: nullableString,
  AD_LocationCode: nullableString,
  AD_StatusAD: nullableString,
  AD_BusinessRegion: nullableString,
  AD_JobFunction: nullableString,
  AD_JobFamily: nullableString,
  TD_EmployeeType: nullableString,
  TD_StatusTD: nullableString,
  TD_Location: nullableString,
  TD_RTM: nullableString,
  TD_Role: nullableString,
  TD_SubRole: nullableString,
  TD_ReportsToHPID: nullableString,
}

export interface IJsonReference {
  value: string
  list: string[]
}

export interface IUserRole {
  RoleId: string
  AppId: string
}

export interface IShortUser {
  FirstName: string
  LastName: string
  Id: string
  UID: string
}

export interface IApiUser {
  AppId: string
  Id: string
  UID: string
  CanSimulateUsers: boolean
  FirstName: string
  LastName: string
  LastLogonTime: string
  Country: string // IJsonReference
  HpOrg: string // IJsonReference
  Language: string // IJsonReference
  GeoMarket: string // IJsonReference
  Locale: string
  Avatar: IUserAvatar | null
  ParsedAvatar: null // ??
  Roles: IUserRole[]
  Type: string
  Password: string
  Created: string
  Modified: string
  PortalUserData: string // ??
  CanBeSimulated: string
  AD_Manager: string | null
  AD_ManagerName: string | null
  AD_LocationCode: string | null
  AD_StatusAD: string | null
  AD_BusinessRegion: string | null
  AD_JobFunction: string | null
  AD_JobFamily: string | null
  TD_EmployeeType: string | null
  TD_StatusTD: string | null
  TD_Location: string | null
  TD_RTM: string | null
  TD_Role: string | null
  TD_SubRole: string | null
  TD_ReportsToHPID: string | null
  ExceptionStatus: string | null // IJsonReference
}

// TODO: REMOVE
export type TUser = typeof initialData

export const create = (data?: Partial<IUser>): IUser => ({
  ...initialData,
  roleIds: [],
  ...data,
})

export const map = (data: IApiUser): IUser => {
  let country = ''
  let countryObj = { value: '', list: [] }
  if (data.Country !== undefined) {
    try {
      countryObj = parseData(data.Country) || { value: '', list: [] }
      if (countryObj.value) {
        country = countryObj.value
      } else if (countryObj.list.length) {
        country = countryObj.list[0]
      }
    } catch (e) {
      Logger.warn('Parse fail:', data.Country)
    }
  }

  let hpOrg = ''
  let hpOrgObj = { value: '', list: [] }
  if (data.HpOrg !== undefined) {
    try {
      hpOrgObj = parseData(data.HpOrg) || { value: '', list: [] }
      if (hpOrgObj.value) {
        hpOrg = hpOrgObj.value
      } else if (hpOrgObj.list.length) {
        hpOrg = hpOrgObj.list[0]
      }
    } catch (e) {
      Logger.warn('Parse fail:', data.HpOrg)
    }
  }

  let geoMarket = ''
  let geoMarketObj = { value: '', list: [] }
  if (data.GeoMarket !== undefined) {
    try {
      geoMarketObj = parseData(data.GeoMarket) || { value: '', list: [] }
      if (geoMarketObj.value) {
        geoMarket = geoMarketObj.value
      } else if (geoMarketObj.list.length) {
        geoMarket = geoMarketObj.list[0]
      }
    } catch (e) {
      Logger.warn('Parse fail:', data.GeoMarket)
    }
  }

  let language = ''
  let languagesObj = { value: '', list: [] }
  if (data.Language !== undefined) {
    try {
      languagesObj = parseData(data.Language) || { value: '', list: [] }
      if (languagesObj.value) {
        language = languagesObj.value
      } else if (languagesObj.list.length) {
        language = languagesObj.list[0]
      }
    } catch (e) {
      Logger.warn('Parse fail:', data.Language)
    }
  }

  let locale = ''
  let localeObj = { value: '', list: [] }
  if (data.Locale !== undefined) {
    try {
      localeObj = parseData(data.Locale) || { value: '', list: [] }
      if (localeObj.value) {
        locale = localeObj.value
      } else if (localeObj.list.length) {
        locale = localeObj.list[0]
      }
    } catch (e) {
      Logger.warn('Parse fail Locale:', data.Locale)
    }
  }

  let exceptionStatus = null
  if (data.ExceptionStatus) {
    try {
      exceptionStatus = data.ExceptionStatus
        ? parseData<IExceptionStatus[]>(data.ExceptionStatus, 'array')
        : null
    } catch (e) {
      Logger.warn('Parse fail exceptionStatus:', data.ExceptionStatus)
    }
  }

  let ssoData: { [key: string]: string }[] = []
  if (data.PortalUserData !== undefined) {
    try {
      ssoData = parseData(data.PortalUserData)
    } catch (e) {
      Logger.warn('Parse fail:', data.PortalUserData)
    }
  }

  return {
    country,
    hpOrg,
    geoMarket,
    language,
    ssoData,
    canSimulateUsers: data.CanSimulateUsers,
    id: (data.Id || initialData.id)?.trim(),
    email: data.Id || initialData.email,
    firstName: data.FirstName || initialData.firstName,
    lastName: data.LastName || initialData.lastName,
    locale,
    locales:
      localeObj.list && Array.isArray(localeObj.list) ? [...localeObj.list].filter((i) => i) : [],
    countries:
      countryObj.list && Array.isArray(countryObj.list)
        ? [...countryObj.list].filter((i) => i)
        : [],
    hpOrgs:
      hpOrgObj.list && Array.isArray(hpOrgObj.list) ? [...hpOrgObj.list].filter((i) => i) : [],
    geoMarkets:
      geoMarketObj.list && Array.isArray(geoMarketObj.list)
        ? [...geoMarketObj.list].filter((i) => i)
        : [],
    languages:
      languagesObj.list && Array.isArray(languagesObj.list)
        ? [...languagesObj.list].filter((i) => i)
        : [],
    roleIds: arrayUnique([...filterRolesByApp(data.Roles)]),
    password: '',
    canBeSimulated: data.CanBeSimulated,
    lastLogonTime: data.LastLogonTime,
    avatar: data.Avatar,
    UID: data.UID,
    exceptionStatus: exceptionStatus,
    AD_Manager: data.AD_Manager,
    AD_ManagerName: data.AD_ManagerName,
    AD_LocationCode: data.AD_LocationCode,
    AD_StatusAD: data.AD_StatusAD,
    AD_BusinessRegion: data.AD_BusinessRegion,
    AD_JobFunction: data.AD_JobFunction,
    AD_JobFamily: data.AD_JobFamily,
    TD_EmployeeType: data.TD_EmployeeType,
    TD_StatusTD: data.TD_StatusTD,
    TD_Location: data.TD_Location,
    TD_RTM: data.TD_RTM,
    TD_Role: data.TD_Role,
    TD_SubRole: data.TD_SubRole,
    TD_ReportsToHPID: data.TD_ReportsToHPID,
  }
}

export const getUserCultureCode = (user: IUser) => {
  return `${user.country.toLowerCase()}-${user.language.toLowerCase()}`
}

const filterRolesByApp = (roles: IUserRole[] | string): string[] => {
  if (!roles || roles.length === 0) {
    return []
  }

  if (typeof roles === 'string') {
    return roles.split(',').map((el) => el.trim())
  }

  const AppId = getAppId()
  return roles
    .map((i) => {
      if (AppId === i.AppId) {
        return i.RoleId
      }
      return ''
    })
    .filter((r) => r !== '')
}
